import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { LogIn, Moon, Sun, ArrowLeft, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OffersPage() {
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [applyingId, setApplyingId] = useState('');
  const [eligibilityByOfferId, setEligibilityByOfferId] = useState({});
  const [loadingEligibility, setLoadingEligibility] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    fetchOffers();
  }, []);

  useEffect(() => {
    hydrateEligibility();
  }, [searchParams, user]);

  const fetchOffers = async () => {
    try {
      setOffersLoading(true);
      const response = await axios.get(`${API}/offers`);
      const normalized = Array.isArray(response.data) ? response.data
        .filter((offer) => offer?.id && offer?.valid_till)
        .map((offer) => ({ ...offer, valid_till: new Date(offer.valid_till).toISOString() }))
        .sort((a, b) => new Date(a.valid_till).getTime() - new Date(b.valid_till).getTime()) : [];
      setOffers(normalized);
    } catch (error) {
      console.error('Failed to fetch offers:', error);
      toast.error('Unable to load offers right now');
    } finally {
      setOffersLoading(false);
    }
  };

  const hasCartContext = useMemo(() => Boolean(searchParams.get('cart_id') || localStorage.getItem('selected_cart_id')), [searchParams]);

  const hydrateEligibility = async () => {
    const cartId = searchParams.get('cart_id') || localStorage.getItem('selected_cart_id');
    if (!cartId || !user) {
      setEligibilityByOfferId({});
      setLoadingEligibility(false);
      return;
    }

    try {
      setLoadingEligibility(true);
      const response = await axios.get(`${API}/carts/${cartId}/offers/eligibility`);
      const data = Array.isArray(response.data) ? response.data : [];
      const mapped = data.reduce((acc, row) => {
        if (row?.offer_id) {
          acc[row.offer_id] = {
            can_apply: row.can_apply !== false,
            reason: row.reason || 'Offer is not applicable for this cart',
          };
        }
        return acc;
      }, {});
      setEligibilityByOfferId(mapped);
    } catch (error) {
      console.error('Failed to fetch offer eligibility:', error);
      setEligibilityByOfferId({});
    } finally {
      setLoadingEligibility(false);
    }
  };


  const applyOfferToCart = async (offerId) => {
    if (!user) {
      navigate('/user-login');
      return;
    }
    const cartId = searchParams.get('cart_id') || localStorage.getItem('selected_cart_id');
    if (!cartId) {
      toast.error('Open this page from Cart to apply offers');
      return;
    }
    const eligibility = eligibilityByOfferId[offerId];
    if (eligibility?.can_apply === false) {
      toast.error(eligibility.reason || 'Offer is not applicable for this cart');
      return;
    }
    try {
      setApplyingId(offerId);
      await axios.post(`${API}/carts/${cartId}/apply-offer`, { offer_id: offerId });
      toast.success('Offer applied to cart');
      navigate('/cart');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Offer is not applicable for this cart');
    } finally {
      setApplyingId('');
    }
  };

  const getTimeRemaining = (validTill) => {
    const now = new Date();
    const end = new Date(validTill);
    const diff = end - now;

    if (diff <= 0) return 'Expired';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h remaining`;
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#020617]' : 'bg-slate-50'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${theme === 'dark' ? 'glassmorphism' : 'bg-white/90 backdrop-blur-sm border-b border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/cart')}
              variant="ghost"
              size="icon"
              data-testid="back-to-menu-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className={`font-serif text-2xl md:text-3xl ${theme === 'dark' ? 'text-amber-50' : 'text-slate-900'}`} data-testid="offers-page-title">
              Special Offers
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={toggleTheme}
              variant="ghost"
              size="icon"
              className={theme === 'dark' ? 'text-amber-50 hover:text-amber-400' : 'text-slate-900 hover:text-amber-600'}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Offers Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {offersLoading && (
            Array.from({ length: 4 }).map((_, idx) => (
              <Card key={`offer-skeleton-${idx}`} className={`${theme === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'} overflow-hidden`}>
                <Skeleton className="aspect-video w-full" />
                <CardContent className="p-6 space-y-3">
                  <Skeleton className="h-8 w-2/3" />
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))
          )}
          {!offersLoading && offers.map((offer, index) => (
            <motion.div
              key={offer.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {(() => {
                const eligibility = eligibilityByOfferId[offer.id];
                const canApply = eligibility ? eligibility.can_apply !== false : true;
                const disabled = applyingId === offer.id || !hasCartContext || !canApply;
                const reasonText = eligibility?.reason;
                return (
              <Card className={`overflow-hidden ${theme === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`} data-testid={`offer-card-${offer.id}`}>
                {/* Offer Image */}
                <div className="aspect-video overflow-hidden">
                  <img
                    src={offer.image_url || offer.image_data || 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800'}
                    alt={offer.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6 space-y-4">
                  {/* Offer Name */}
                  <div>
                    <h2 className={`font-serif text-2xl font-bold ${theme === 'dark' ? 'text-amber-50' : 'text-slate-900'}`} data-testid={`offer-name-${offer.id}`}>
                      {offer.name}
                    </h2>
                  </div>

                  {/* Countdown */}
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <Badge className="bg-amber-500 text-black" data-testid={`offer-countdown-${offer.id}`}>
                      {getTimeRemaining(offer.valid_till)}
                    </Badge>
                  </div>

                  {/* Description */}
                  <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>
                    {offer.description}
                  </p>

                  {/* Terms and Conditions */}
                  <div>
                    <h3 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-amber-100' : 'text-slate-900'}`}>
                      Terms & Conditions:
                    </h3>
                    <p className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                      {offer.terms_conditions}
                    </p>
                  </div>

                  {loadingEligibility && hasCartContext && !eligibility && (
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  )}

                  {!loadingEligibility && hasCartContext && (
                    <Badge className={canApply ? 'bg-emerald-500 text-white' : 'bg-slate-400 text-white'}>
                      {canApply ? 'Eligible' : 'Not eligible'}
                    </Badge>
                  )}

                  <Button
                    className={`w-full ${disabled ? 'bg-slate-400 hover:bg-slate-400 text-slate-100 cursor-not-allowed' : ''}`}
                    disabled={disabled}
                    onClick={() => applyOfferToCart(offer.id)}
                  >
                    {applyingId === offer.id ? 'Applying...' : hasCartContext ? (canApply ? 'Apply to Cart' : 'Cannot Apply') : 'Open via Cart to Apply'}
                  </Button>
                  {!canApply && reasonText && (
                    <button
                      type="button"
                      onClick={() => toast.info(reasonText)}
                      className={`text-left text-xs underline ${theme === 'dark' ? 'text-amber-300' : 'text-slate-600'}`}
                    >
                      Why can&apos;t I apply this?
                    </button>
                  )}

                  <p className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-500'}`}>
                    Valid till: {new Date(offer.valid_till).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </CardContent>
              </Card>
                );
              })()}
            </motion.div>
          ))}
        </motion.div>

        {offers.length === 0 && (
          <div className="text-center py-16" data-testid="no-offers-message">
            <p className={`text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
              No active offers at the moment.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
